<script setup lang="ts">
import { computed } from 'vue';

import type {
  AppButtonType,
  AppButtonState,
  AppButtonSize,
  AppButtonIconProperties,
} from '../appButton/AppButton.model';
import AppSvgIcon from '../appSvgIcon/AppSvgIcon.vue';
import { AppSpinner, AppLoadingDots } from '../appLoaders/index';
import AppRawButton from '../appRawButton/AppRawButton.vue';

interface AppIconButtonProps {
  type: AppButtonType;
  state: AppButtonState;
  size: AppButtonSize;
  icon: AppButtonIconProperties;
  ariaLabel: string;
}

const props = defineProps<AppIconButtonProps>();

interface AppIconButtonEmits {
  (eventName: 'click', event: MouseEvent): void;
}

const emit = defineEmits<AppIconButtonEmits>();

const iconEffectiveSize = computed(() => {
  if (props.icon.size) {
    return props.icon.size;
  }
  return props.size === 'small' ? '18' : '24';
});

function onClick(event: MouseEvent): void {
  if (props.state === 'enabled') {
    emit('click', event);
  }
}
</script>

<template>
  <AppRawButton
    class="app-icon-button"
    :disabled="props.state !== 'enabled'"
    :class="[props.type, props.size, props.state]"
    :aria-label="props.ariaLabel"
    @click="onClick"
  >
    <AppSpinner
      v-if="props.state === 'loadingSpinner'"
      :size="iconEffectiveSize"
    />
    <AppLoadingDots v-else-if="props.state === 'loadingDots'" type="white" />
    <AppSvgIcon v-else :name="props.icon.name" :size="iconEffectiveSize" />
  </AppRawButton>
</template>

<style scoped lang="scss">
@use '../../styles/_button-mixins.scss' as *;

.app-icon-button {
  @include button-base;
  padding: 0;
  aspect-ratio: 1 / 1;

  &.important {
    @include button-type-important;
  }
  &.primary {
    @include button-type-primary;
  }
  &.secondary {
    @include button-type-secondary;
  }
  &.modern {
    @include button-type-modern;
  }
  &.text {
    @include button-type-text;
  }

  &.small {
    @include app-icon-button-size-small;
  }
  &.medium {
    @include app-icon-button-size-medium;
  }
  &.large {
    @include app-icon-button-size-large;
  }
}
</style>
